// Kid onboarding — Step 4: Pick a pal (mascot). Sets kids.mascot, then
// flips accounts.onboarding_state to DIAGNOSTIC_PENDING so the resolver
// routes to the diagnostic.

import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useAccount, useKids } from "@/lib/account";
import { useOnboardingDraft } from "@/lib/onboardingDraft";
import { kidStepProgress } from "@/lib/kidOnboarding";
import { Pal } from "@/components/Pal";
import { PAL_SLUGS, palFromSlug, type PalSlug } from "@/lib/pal";

export const Route = createFileRoute("/onboarding/kid/pal")({
  head: () => ({ meta: [{ title: "PencilWise · Pick a pal" }] }),
  component: KidPal,
});

const PALS = PAL_SLUGS.map((id) => ({ id, ...palFromSlug(id) }));

// H-A4 shared step styles (H-0 tokens) — see onboarding.kid.name.tsx.
// NB e2e contract (18/19): pal tiles must stay the FIRST buttons in the
// DOM (specs click `button:not(:has-text("continue")).first()`); don't
// add any button above the tile grid.
const EYEBROW = "text-xs font-bold uppercase tracking-[0.18em] text-terracotta-deep";
const CTA =
  "flex min-h-[52px] w-full scroll-mb-28 items-center justify-center rounded-2xl bg-primary px-6 text-base font-extrabold text-primary-foreground shadow-press transition-all active:translate-y-0.5 active:bg-primary-pressed active:shadow-none disabled:opacity-50";

function KidPal() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: account } = useAccount();
  const { data: kids = [] } = useKids();
  const kid = kids[kids.length - 1] ?? null;
  const [picked, setPicked] = useState<PalSlug | null>(
    (kid?.mascot as PalSlug) ?? null,
  );
  const [busy, setBusy] = useState(false);
  const { current, total } = kidStepProgress("pal");

  const { setDraft } = useOnboardingDraft();
  const submit = async () => {
    if (!picked) return;
    setBusy(true);
    // Phase G3 Stage 4: persistence at handoff via the gate-ladder
    // bridge; per-step supabase writes skipped (mock no-op anyway).
    setDraft({ mascotId: picked });
    // T1.1a: route through the new parent-priors step before handoff.
    navigate({ to: "/onboarding/kid/abilities", search: (prev) => prev });
  };

  return (
    <div className="relative min-h-screen bg-cream paper-grain">
      <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6 pt-10 pb-32">
        <header className="pointer-events-none animate-slide-up">
          <p className={EYEBROW}>
            Step {current} of {total}
          </p>
          <h1 className="mt-2 font-serif text-3xl font-bold tracking-tight">
            Pick a learning pal.
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            Your pal will cheer you on and explain new ideas.
          </p>
        </header>

        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {PALS.map((p) => {
            const active = picked === p.id;
            return (
              <button
                key={p.id}
                onClick={() => setPicked(p.id)}
                aria-pressed={active}
                className={`flex flex-col items-center rounded-3xl border-2 p-3 text-center transition-all active:scale-[0.97] ${
                  active
                    ? "border-primary bg-primary/10"
                    : "border-border bg-card shadow-soft hover:border-primary"
                }`}
              >
                <Pal color={p.color} mood={active ? "wave" : "happy"} size={64} />
                <p className="mt-1 font-serif text-base font-bold">{p.name}</p>
                <p className="text-xs leading-tight text-muted-foreground">
                  {p.tagline}
                </p>
              </button>
            );
          })}
        </div>

        <button onClick={submit} disabled={!picked || busy} className={`mt-6 ${CTA}`}>
          {busy ? "Saving…" : "Continue →"}
        </button>
      </div>
    </div>
  );
}
